// As partes do alvo que NÃO seguem a regra em edição (design D13, `overrides` da prévia de
// impacto): regras mais específicas dentro do serviço, subetapa ou etapa. O motor dá
// precedência à regra mais específica, então essas partes ficam com a forma delas — e o
// cliente precisa vê-las para analisar e corrigir, não descobrir depois na curva.
//
// "Editar regra" abre o editor da regra que governa a parte. Com `onDecide` (D19), o bloco
// também pede a decisão antes de salvar.
//
// Revisão de 2026-10-13 (design D5/D6): as regras chegam em dois grupos, cada um com a sua
// decisão (`overrides[].targetKind`, contrato C2):
//
// - regras de EAP de nível menor (subetapa ou serviço dentro do alvo): preservar ou
//   sobrescrever (desativadas ao salvar, `overrideRuleIds`);
// - regras de insumo, grupo Ampli ou grupo do ERP: manter, aplicar a regra do alvo
//   (desativadas ao salvar, `overrideRuleIds`) ou as duas (dentro do alvo vale a regra do
//   alvo, fora dele a do insumo continua; `prevailsOverRuleIds`).
//
// O salvar espera uma decisão em cada grupo presente.
import { describeProfile } from "./profile";
import { TargetValue } from "./RuleTargetPicker";

export const OVERRIDE_DECISIONS = Object.freeze({
  keep: "keep",
  override: "override",
  both: "both",
});

export const OVERRIDE_GROUPS = Object.freeze({ node: "node", resource: "resource" });

export const EMPTY_OVERRIDE_DECISIONS = Object.freeze({ node: null, resource: null });

/** O grupo da regra: `targetKind` do contrato; sem ele, o `kind` do alvo (payload anterior). */
export const overrideGroupOf = (override) => {
  const kind = override?.targetKind ?? override?.target?.kind;
  return kind === "node" ? OVERRIDE_GROUPS.node : OVERRIDE_GROUPS.resource;
};

const NODE_LEVEL_LABELS = Object.freeze({
  cell: "Célula",
  step: "Etapa",
  substep: "Subetapa",
  service: "Serviço",
});

const kindLabel = (override) => {
  const target = override?.target;
  const key = String(target?.key ?? override?.ruleKey ?? "");
  const kind = override?.targetKind ?? target?.kind;
  if (kind === "resource") return "Insumo";
  if (kind === "ampli_group" || key.startsWith("resource_group:ampli:")) return "Grupo Ampli";
  if (kind === "erp_group" || key.startsWith("resource_group:")) return "Grupo do ERP";
  if (kind === "node") return NODE_LEVEL_LABELS[override?.level ?? target?.level] ?? "EAP";
  return "Regra";
};

const nameOf = (override) => override?.target?.label ?? override?.label ?? override?.ruleKey;

const profileText = (profile) => {
  try {
    return describeProfile(profile);
  } catch {
    return "Forma indisponível";
  }
};

const centsOf = (list) =>
  list.reduce((total, override) => total + (Number(override.redistributedCents) || 0), 0);

const idsOf = (list) => list.map((override) => override.ruleId).filter(Boolean);

/**
 * O que as escolhas significam para o salvar e para a prévia — uma função pura, a mesma para
 * o painel e para os testes:
 *
 * - `nodes`/`resources`: as regras de cada grupo;
 * - `decisions`: as escolhas efetivas (sem `canOverride`, a proposta de IA, vale manter);
 * - `pending`: o grupo presente ainda sem escolha;
 * - `overrideRuleIds`/`prevailsOverRuleIds`: o corpo do salvar (contrato C3);
 * - `keptCents`: o valor que continua com as regras mais específicas (a regra do alvo
 *   distribui o resto); grupo sem escolha conta como mantido, que é a precedência de hoje;
 * - `governsAll`: nenhuma regra mais específica fica com valor dentro do alvo;
 * - `scenario`: a chave de `targetSeries` da prévia para a combinação escolhida, ou `null`
 *   quando a prévia não traz essa combinação (EAP e insumo com escolhas que não se alinham).
 */
export const overridePlan = (overrides, decisions = EMPTY_OVERRIDE_DECISIONS, { canOverride = true } = {}) => {
  const list = Array.isArray(overrides) ? overrides : [];
  const nodes = list.filter((override) => overrideGroupOf(override) === OVERRIDE_GROUPS.node);
  const resources = list.filter((override) => overrideGroupOf(override) === OVERRIDE_GROUPS.resource);
  const effective = canOverride
    ? { node: decisions?.node ?? null, resource: decisions?.resource ?? null }
    : { node: OVERRIDE_DECISIONS.keep, resource: OVERRIDE_DECISIONS.keep };
  const node = nodes.length > 0 ? effective.node : null;
  const resource = resources.length > 0 ? effective.resource : null;
  const pending = {
    node: nodes.length > 0 && !node,
    resource: resources.length > 0 && !resource,
  };
  const overrideRuleIds = [
    ...(node === OVERRIDE_DECISIONS.override ? idsOf(nodes) : []),
    ...(resource === OVERRIDE_DECISIONS.override ? idsOf(resources) : []),
  ];
  const prevailsOverRuleIds = resource === OVERRIDE_DECISIONS.both ? idsOf(resources) : [];
  const keptNodes = node === OVERRIDE_DECISIONS.override ? [] : nodes;
  const keptResources = resource === OVERRIDE_DECISIONS.override || resource === OVERRIDE_DECISIONS.both
    ? []
    : resources;
  const scenario = (() => {
    if (list.length === 0) return "keep";
    if (pending.node || pending.resource) return null;
    if (nodes.length === 0) return resource;
    if (resources.length === 0) return node;
    if (node === OVERRIDE_DECISIONS.keep && resource === OVERRIDE_DECISIONS.keep) return "keep";
    if (node === OVERRIDE_DECISIONS.override && resource !== OVERRIDE_DECISIONS.keep) return resource;
    return null;
  })();
  return {
    nodes,
    resources,
    decisions: { node, resource },
    pending,
    hasPending: pending.node || pending.resource,
    overrideRuleIds,
    prevailsOverRuleIds,
    keptCents: centsOf(keptNodes) + centsOf(keptResources),
    governsAll: list.length > 0 && keptNodes.length === 0 && keptResources.length === 0,
    scenario,
  };
};

/**
 * A série resultante do servidor para as escolhas do plano. As combinações simples saem de
 * `targetSeries[scenario]`; com regras de EAP e de insumo no mesmo alvo, as que não se
 * alinham vêm de `targetSeries.byDecision[<EAP>][<insumo>]` (EAP `preserve`/`override` ×
 * insumo `keep`/`apply`/`both`). `null` quando a prévia não traz a combinação.
 */
export const resultingSeriesOf = (targetSeries, plan) => {
  if (!targetSeries || !plan || plan.hasPending) return null;
  if (plan.scenario) return targetSeries[plan.scenario] ?? null;
  const { node, resource } = plan.decisions ?? {};
  if (!node || !resource) return null;
  const nodeKey = node === OVERRIDE_DECISIONS.override ? "override" : "preserve";
  const resourceKey = resource === OVERRIDE_DECISIONS.override
    ? "apply"
    : resource === OVERRIDE_DECISIONS.both ? "both" : "keep";
  return targetSeries.byDecision?.[nodeKey]?.[resourceKey] ?? null;
};

/** O que falta para salvar, por grupo; `null` sem nada pendente. */
export const pendingDecisionText = (plan) => {
  if (plan.pending.node && plan.pending.resource)
    return "Escolha como tratar as regras da EAP e as de insumo e grupo neste alvo.";
  if (plan.pending.node) return "Escolha como tratar as regras da EAP dentro deste alvo.";
  if (plan.pending.resource) return "Escolha como tratar os insumos com regra própria neste alvo.";
  return null;
};

const OverrideRow = ({ override, onEdit }) => (
  <li className="ampli-rule-overrides__row">
    <div className="ampli-rule-overrides__main">
      <span className="ampli-rule-overrides__kind">{kindLabel(override)}</span>
      <strong>{nameOf(override)}</strong>
      <span className="ampli-rule-overrides__detail">
        {`${profileText(override.profile)} · ${override.serviceCount} ${
          override.serviceCount === 1 ? "serviço" : "serviços"
        }`}
      </span>
    </div>
    <span className="ampli-rule-overrides__value">
      <TargetValue cents={override.redistributedCents} />
    </span>
    {onEdit && override.ruleId ? (
      <button
        type="button"
        className="button button-outline small"
        aria-label={`Editar regra de ${nameOf(override)}`}
        onClick={() => onEdit(override)}
      >
        Editar regra
      </button>
    ) : null}
  </li>
);

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

const Choice = ({ group, value, decision, onDecide, disabled = false, title, children }) => (
  <label className="ampli-rule-overrides__choice">
    <input
      type="radio"
      name={`ampli-rule-override-decision-${group}`}
      value={value}
      checked={decision === value}
      disabled={disabled}
      onChange={() => onDecide(group, value)}
    />
    <span className="ampli-rule-overrides__choice-text">
      <strong>{title}</strong>
      {children}
    </span>
  </label>
);

const AI_WARNING = (
  <span className="ampli-rule-overrides__warning">
    Descarte a proposta de IA para mudar as regras mais específicas.
  </span>
);

// EAP: preservar ou sobrescrever.
const NodeDecision = ({ nodes, decision, onDecide, canOverride }) => (
  <fieldset className="ampli-rule-overrides__decision" data-testid="rule-overrides-node-decision">
    <legend>Como tratar as regras da EAP?</legend>
    <Choice
      group={OVERRIDE_GROUPS.node}
      value={OVERRIDE_DECISIONS.keep}
      decision={decision}
      onDecide={onDecide}
      title="Preservar as regras da EAP"
    >
      <span>
        {"Elas continuam governando "}
        <TargetValue cents={centsOf(nodes)} />
        {"; esta regra distribui só o restante."}
      </span>
    </Choice>
    <Choice
      group={OVERRIDE_GROUPS.node}
      value={OVERRIDE_DECISIONS.override}
      decision={decision}
      onDecide={onDecide}
      disabled={!canOverride}
      title="Sobrescrever com esta regra"
    >
      <span>
        {`${
          nodes.length === 1
            ? "A regra acima é desativada"
            : `As ${nodes.length} regras acima são desativadas`
        } ao salvar (ficam no histórico e podem ser reativadas), e o valor delas segue esta forma.`}
      </span>
      {!canOverride ? AI_WARNING : null}
    </Choice>
  </fieldset>
);

// Insumo e grupo: manter, aplicar ou as duas.
const ResourceDecision = ({ resources, decision, onDecide, canOverride }) => {
  const outside = resources.filter((override) => override.outsideServiceCount > 0);
  return (
    <fieldset className="ampli-rule-overrides__decision" data-testid="rule-overrides-resource-decision">
      <legend>Como tratar as regras de insumo e grupo?</legend>
      <Choice
        group={OVERRIDE_GROUPS.resource}
        value={OVERRIDE_DECISIONS.keep}
        decision={decision}
        onDecide={onDecide}
        title="Manter as regras próprias"
      >
        <span>
          {"Os insumos seguem a forma deles em toda a obra, inclusive aqui ("}
          <TargetValue cents={centsOf(resources)} />
          {")."}
        </span>
      </Choice>
      <Choice
        group={OVERRIDE_GROUPS.resource}
        value={OVERRIDE_DECISIONS.override}
        decision={decision}
        onDecide={onDecide}
        disabled={!canOverride}
        title="Aplicar esta regra a todo o valor"
      >
        <span>
          {`${
            resources.length === 1
              ? "A regra acima é desativada"
              : `As ${resources.length} regras acima são desativadas`
          } ao salvar (ficam no histórico e podem ser reativadas) e deixam de valer em toda a obra.`}
        </span>
        {outside.map((override) => (
          <span
            key={override.ruleKey}
            className="ampli-rule-overrides__warning"
            data-testid="rule-override-outside"
          >
            {`${nameOf(override)} também vale em ${plural(
              override.outsideServiceCount,
              "serviço",
              "serviços",
            )} fora deste alvo, que deixam de seguir a regra.`}
          </span>
        ))}
        {!canOverride ? AI_WARNING : null}
      </Choice>
      <Choice
        group={OVERRIDE_GROUPS.resource}
        value={OVERRIDE_DECISIONS.both}
        decision={decision}
        onDecide={onDecide}
        disabled={!canOverride}
        title="As duas: esta regra aqui dentro, a do insumo fora"
      >
        <span>
          Dentro deste alvo vale esta regra; fora dele a regra do insumo continua valendo, sem ser
          desativada.
        </span>
        {outside.map((override) => (
          <span
            key={override.ruleKey}
            className="ampli-rule-overrides__note"
            data-testid="rule-override-outside-kept"
          >
            {`${nameOf(override)} continua valendo nos ${plural(
              override.outsideServiceCount,
              "serviço",
              "serviços",
            )} fora deste alvo.`}
          </span>
        ))}
        {!canOverride ? AI_WARNING : null}
      </Choice>
    </fieldset>
  );
};

const Group = ({ title, testId, overrides, onEdit, children }) => (
  <div className="ampli-rule-overrides__group" data-testid={testId}>
    <h4>{title}</h4>
    <ul>
      {overrides.map((override) => (
        <OverrideRow key={override.ruleKey} override={override} onEdit={onEdit} />
      ))}
    </ul>
    {children}
  </div>
);

// Um estado por vez: carregando, falha, nenhuma exceção ou os grupos.
const OverridesBody = ({ status, overrides, onEdit, decisionProps }) => {
  if (status === "loading") {
    return (
      <p className="card-label" role="status">
        Verificando regras mais específicas neste alvo…
      </p>
    );
  }
  if (status === "error") {
    return (
      <p className="card-label" role="status">
        Não foi possível verificar as regras mais específicas agora.
      </p>
    );
  }
  if (overrides.length === 0) {
    return (
      <p className="card-label" data-testid="rule-overrides-empty">
        Nenhuma parte deste alvo tem regra mais específica: todo o valor segue esta regra.
      </p>
    );
  }
  const plan = overridePlan(overrides, decisionProps?.decisions, {
    canOverride: decisionProps?.canOverride ?? true,
  });
  return (
    <>
      {decisionProps ? (
        <p className="ampli-rule-overrides__lead">
          {"Estas partes têm regra própria, mais específica, com outra forma. "}
          <strong>Escolha abaixo</strong>
          {plan.nodes.length > 0 && plan.resources.length > 0
            ? " como tratar as regras da EAP e as de insumo e grupo."
            : " se elas mantêm a forma delas ou passam a seguir esta regra."}
        </p>
      ) : (
        <p className="ampli-rule-overrides__lead">
          {"Estas partes seguem a regra própria delas, que é mais específica, e "}
          <strong>não mudam com esta regra</strong>
          {". Confira se a forma de cada uma faz sentido e corrija se precisar."}
        </p>
      )}
      {plan.nodes.length > 0 ? (
        <Group
          title="Etapas, subetapas e serviços com regra própria"
          testId="rule-overrides-nodes"
          overrides={plan.nodes}
          onEdit={onEdit}
        >
          {decisionProps ? (
            <NodeDecision
              nodes={plan.nodes}
              decision={plan.decisions.node}
              onDecide={decisionProps.onDecide}
              canOverride={decisionProps.canOverride}
            />
          ) : null}
        </Group>
      ) : null}
      {plan.resources.length > 0 ? (
        <Group
          title="Insumos e grupos com regra própria"
          testId="rule-overrides-resources"
          overrides={plan.resources}
          onEdit={onEdit}
        >
          {decisionProps ? (
            <ResourceDecision
              resources={plan.resources}
              decision={plan.decisions.resource}
              onDecide={decisionProps.onDecide}
              canOverride={decisionProps.canOverride}
            />
          ) : null}
        </Group>
      ) : null}
      {decisionProps && !plan.hasPending && plan.keptCents > 0
        && decisionProps.targetCents > 0 && plan.keptCents >= decisionProps.targetCents ? (
          <p
            className="ampli-rule-overrides__notice"
            role="status"
            data-testid="rule-overrides-nothing-left"
          >
            As regras mais específicas cobrem todo o valor deste alvo: mantendo-as, esta regra não
            move nada.
          </p>
        ) : null}
    </>
  );
};

const RuleOverridesSection = ({
  impact,
  onEditOverride,
  // D19: a decisão é pedida quando o editor passa `onDecide(grupo, escolha)`.
  decisions = EMPTY_OVERRIDE_DECISIONS,
  onDecide,
  targetCents = 0,
  canOverride = true,
}) => {
  if (!impact || impact.status === "idle" || impact.status === "unavailable")
    return null;
  const overrides = impact.answer?.overrides ?? [];
  return (
    <section
      className="ampli-rule-overrides"
      aria-labelledby="ampli-rule-overrides-title"
    >
      <h3 id="ampli-rule-overrides-title">
        Regras mais específicas neste alvo
      </h3>
      <OverridesBody
        status={impact.status}
        overrides={overrides}
        onEdit={onEditOverride}
        decisionProps={
          onDecide
            ? { decisions, onDecide, targetCents, canOverride }
            : null
        }
      />
    </section>
  );
};

export default RuleOverridesSection;
